import { fmtNum, fmtPrice } from "@/lib/api";

const short = (name) => name.replace(/ \(.*\)$/, "").replace(/ Score$/, "");
const tone = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-slate-500");
const Th = ({ children, left, title }) => (
  <th title={title} className={`px-2 py-2.5 font-normal whitespace-nowrap ${left ? "text-left" : "text-right"}`}>{children}</th>
);

export const FinalRatingsTable = ({ ratings, matrices }) => {
  const maxPct = Math.max(1, ...ratings.map((r) => r.pct));
  const sumChance = ratings.reduce((s, r) => s + (r.chance || 0), 0);
  return (
    <div className="scroll-x">
      <p className="text-xs num text-slate-400 mb-3" data-testid="raw-score-formula">
        Raw Score = {matrices.map((m) => short(m.name)).join(" + ")} <span className="text-slate-500">(nearest 0.5)</span> → Rating = 100 − Diff → Chance from table → % = Chance ÷ {fmtNum(sumChance, 2)} → $ = 100 ÷ %
      </p>
      <table className="w-full text-sm" data-testid="final-ratings-table">
        <thead>
          <tr className="eyebrow border-b border-slate-800">
            <Th left>Rank</Th>
            <Th left>Runner</Th>
            {matrices.map((m) => <Th key={m.id} title={m.name}>{short(m.name)}</Th>)}
            <Th>Raw</Th>
            <Th title="Top Raw Score = 100">Rating</Th>
            <Th title="Top Raw Score − Raw Score">Diff</Th>
            <Th title="From your difference → chance table">Chance</Th>
            <th className="px-2 py-2.5 font-normal min-w-[130px] text-left">% Chance</th>
            <Th>$ Price</Th>
            <Th>Market $</Th>
          </tr>
        </thead>
        <tbody>
          {ratings.map((r, i) => (
            <tr key={r.name} data-testid={`final-row-${i}`} className={`row-hover border-b border-slate-800/60 ${r.rank === 1 ? "bg-emerald-500/[0.06]" : ""}`}>
              <td className="px-2 py-2.5">
                <span className={`num inline-grid place-items-center h-6 w-6 rounded text-xs font-bold ${r.rank === 1 ? "bg-emerald-500 text-[#080C14]" : "bg-slate-800 text-slate-300"}`}>{r.rank}</span>
              </td>
              <td className="px-2 py-2.5 whitespace-nowrap">
                <span className="num text-slate-500 mr-2">{r.number ?? ""}</span>
                <span className="text-white font-medium">{r.name}</span>
              </td>
              {matrices.map((m) => (
                <td key={m.id} className={`px-2 py-2.5 num text-right ${tone(r.scores[m.id]?.weighted)}`}>{fmtNum(r.scores[m.id]?.weighted, 2)}</td>
              ))}
              <td title={`Exact sum: ${r.raw_exact}`} className="px-2 py-2.5 num text-right font-bold text-white" data-testid={`final-rating-${i}`}>{Number(r.raw_score).toFixed(1)}</td>
              <td className="px-2 py-2.5 num text-right font-bold text-emerald-400" data-testid={`final-rating100-${i}`}>{Number(r.rating).toFixed(1)}</td>
              <td className="px-2 py-2.5 num text-right text-slate-300" data-testid={`final-diff-${i}`}>{Number(r.diff).toFixed(1)}</td>
              <td className="px-2 py-2.5 num text-right text-slate-300" data-testid={`final-chance-${i}`}>{fmtNum(r.chance, 2)}</td>
              <td className="px-2 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 flex-1 bg-slate-800 rounded overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${(r.pct / maxPct) * 100}%` }} />
                  </div>
                  <span className="num text-xs w-12 text-right" data-testid={`final-pct-${i}`}>{fmtNum(r.pct, 1)}%</span>
                </div>
              </td>
              <td className="px-2 py-2.5 num text-right font-bold text-amber-400" data-testid={`final-price-${i}`}>{fmtPrice(r.price)}</td>
              <td className="px-2 py-2.5 num text-right whitespace-nowrap">
                {fmtPrice(r.market_price)}
                {r.value && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30" data-testid={`final-value-${i}`}>VALUE</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
